<script setup>
// FLOW-12 (Task 5.1, T1, G7) — um campo do formulário "Dados do teste", pelo `type` do param, como a
// tabela do T1:
// - `string`: texto de uma linha; com `enum`, uma escolha (`CampoDeEscolha`, o `FilterSelect`);
// - `integer` e `number`: número;
// - `boolean`: toggle (o `Switch` do OS);
// - `array`: uma linha por item, do tipo de `items`, com "Adicionar item" e "Excluir" por item;
// - `object`: os campos de `properties`, agrupados sob o nome, em qualquer nível; sem `properties`,
//   o JSON no editor da casa (`CodigoJson`).
// O campo é recursivo: o item da lista e a propriedade do objeto são campos também.
//
// O rótulo é o nome do param em mono, o tipo e "obrigatório" em texto secundário, sem cor; embaixo,
// a `description`. O erro é o do servidor (`erroDoParam`), com o glifo rubi e o texto neutro, no
// campo do `caminho` dele: quem mostra é o campo mais fundo que o contém. Quando mostrar (ao sair do
// campo, ou no "Executar") é de quem monta o formulário (`CamposDoTeste`). A escolha de um `enum`
// também diz que saiu quando o foco deixa ela sem escolher nada (Gate 4, 2ª rodada).
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CampoDeEscolha from './CampoDeEscolha.vue';
import CodigoJson from './CodigoJson.vue';
import AvisoDoFlow from './AvisoDoFlow.vue';
import { TextoDoCompilador } from './textoDoCompilador';

const props = defineProps({
  // A definição do param, da propriedade ou do item (`$defs/param` do schema); nula na chave que só
  // o JSON tem.
  def: { type: Object, default: null },
  // O valor de agora (qualquer JSON, ou `undefined` sem valor).
  valor: { type: [String, Number, Boolean, Array, Object], default: undefined },
  // Onde o valor mora nos params: `['cliente', 'nome']`, `['ids', 1]`.
  caminho: { type: Array, required: true },
  // O nome no rótulo; vazio no item da lista, que não tem rótulo próprio (`rotuloAcessivel` diz).
  rotulo: { type: String, default: '' },
  rotuloAcessivel: { type: String, default: '' },
  obrigatorio: { type: Boolean, default: false },
  // O erro do param inteiro (`{mensagem, caminho}`), ou nulo; o campo mostra o que é dele.
  erro: { type: Object, default: null },
  // O começo dos ids, único na página.
  prefixo: { type: String, default: 'teste' },
});

const emit = defineEmits(['update:valor', 'sair']);

defineOptions({ name: 'CampoDoTeste' });

const { t } = useI18n();

const ehObjeto = v => !!v && typeof v === 'object' && !Array.isArray(v);

// O id do campo pelo caminho: o "Executar" com erro foca o campo do caminho do erro (G13).
const idDoCaminho = (prefixo, caminho) =>
  `${prefixo}-campo-${caminho.map(String).join('-')}`.replace(/[^\w-]/g, '_');
const id = computed(() => idDoCaminho(props.prefixo, props.caminho));

// O tipo: o da definição; sem ela, o do valor.
const tipo = computed(() => {
  if (props.def?.type) return props.def.type;
  if (Array.isArray(props.valor)) return 'array';
  if (ehObjeto(props.valor)) return 'object';
  if (typeof props.valor === 'number') return 'number';
  return typeof props.valor === 'boolean' ? 'boolean' : 'string';
});
const NOMES_DOS_TIPOS = {
  string: () => t('POWER_TOOLS.FLOW_EDITOR.STEP_BOX.TYPES.TEXT'),
  integer: () => t('POWER_TOOLS.FLOW_EDITOR.STEP_BOX.TYPES.INTEGER'),
  number: () => t('POWER_TOOLS.FLOW_EDITOR.STEP_BOX.TYPES.NUMBER'),
  boolean: () => t('POWER_TOOLS.FLOW_EDITOR.STEP_BOX.TYPES.BOOLEAN'),
  array: () => t('POWER_TOOLS.FLOW_EDITOR.STEP_BOX.TYPES.LIST'),
  object: () => t('POWER_TOOLS.FLOW_EDITOR.STEP_BOX.TYPES.OBJECT'),
};
const nomeDoTipo = computed(() => NOMES_DOS_TIPOS[tipo.value]?.() || '');

const opcoes = computed(() =>
  Array.isArray(props.def?.enum) ? props.def.enum : null
);
const propriedades = computed(() =>
  tipo.value === 'object' && ehObjeto(props.def?.properties)
    ? props.def.properties
    : null
);
const obrigatoriasDoObjeto = computed(() =>
  Array.isArray(props.def?.required) ? props.def.required : []
);
const itens = computed(() => (Array.isArray(props.valor) ? props.valor : []));
// O JSON é o editor do objeto sem `properties` e do valor que nenhum outro campo sabe mostrar.
const emJson = computed(
  () =>
    (tipo.value === 'object' && !propriedades.value) ||
    !['string', 'integer', 'number', 'boolean', 'array', 'object'].includes(
      tipo.value
    )
);

// -- o erro: o campo mais fundo que contém o caminho do erro é quem o mostra

const comecaCom = (caminho, prefixo) =>
  prefixo.length <= caminho.length &&
  prefixo.every((parte, i) => parte === caminho[i]);
const filhoMostra = caminho => {
  if (caminho.length <= props.caminho.length || emJson.value) return false;
  const proximo = caminho[props.caminho.length];
  if (tipo.value === 'array') {
    return Number.isInteger(proximo) && proximo < itens.value.length;
  }
  return !!propriedades.value && proximo in propriedades.value;
};
const meuErro = computed(() =>
  props.erro &&
  comecaCom(props.erro.caminho || [], props.caminho) &&
  !filhoMostra(props.erro.caminho || [])
    ? props.erro.mensagem
    : null
);
const idDoErro = computed(() => `${id.value}-erro`);
const descricao = computed(() =>
  typeof props.def?.description === 'string' ? props.def.description : ''
);
const idDaDescricao = computed(() => `${id.value}-dica`);
const descritoPor = computed(
  () =>
    [meuErro.value && idDoErro.value, descricao.value && idDaDescricao.value]
      .filter(Boolean)
      .join(' ') || undefined
);

// -- escrever

const mudar = valor => emit('update:valor', valor);

const textoDoValor = v => (typeof v === 'string' ? v : JSON.stringify(v));
const opcoesDaEscolha = computed(() => [
  { value: '', label: t('POWER_TOOLS.FLOW_EDITOR.TEST.FORM.CHOOSE') },
  ...(opcoes.value || []).map((opcao, i) => ({
    value: i,
    label: textoDoValor(opcao),
  })),
]);
const escolhido = computed(() => {
  const i = (opcoes.value || []).findIndex(
    o => JSON.stringify(o) === JSON.stringify(props.valor)
  );
  return i >= 0 ? i : '';
});
function escolher(indice) {
  mudar(indice === '' ? undefined : opcoes.value[indice]);
  emit('sair');
}

// O toggle é escolha feita: confere na hora, como a escolha da lista.
function alternar(ligado) {
  mudar(ligado);
  emit('sair');
}

// O número vazio tira o valor (o servidor diz se faz falta); o resto vai como o número.
const escreverNumero = texto =>
  mudar(texto === '' || texto === null ? undefined : Number(texto));

// O valor vazio de um item novo, pelo tipo dos itens.
function vazioDo(def) {
  const VAZIOS = {
    string: '',
    boolean: false,
    array: [],
    object: {},
  };
  return def?.type in VAZIOS ? structuredClone(VAZIOS[def.type]) : null;
}
const trocarItem = (i, valor) =>
  mudar(itens.value.map((item, k) => (k === i ? valor : item)));
const excluirItem = i => {
  mudar(itens.value.filter((_, k) => k !== i));
  emit('sair');
};
const adicionarItem = () => mudar([...itens.value, vazioDo(props.def?.items)]);

function trocarPropriedade(nome, valor) {
  const atual = ehObjeto(props.valor) ? props.valor : {};
  const { [nome]: _, ...resto } = atual;
  mudar(valor === undefined ? resto : { ...resto, [nome]: valor });
}

// O JSON do objeto sem `properties`: o texto fica aqui enquanto não lê; lido, vira o valor.
const json = valor =>
  valor === undefined ? '' : JSON.stringify(valor, null, 2);
const textoJson = ref(json(props.valor));
const jsonQuebrado = ref('');
watch(
  () => props.valor,
  valor => {
    let atual;
    try {
      atual = textoJson.value.trim() ? JSON.parse(textoJson.value) : undefined;
    } catch {
      atual = Symbol('quebrado');
    }
    if (JSON.stringify(atual) !== JSON.stringify(valor)) {
      textoJson.value = json(valor);
      jsonQuebrado.value = '';
    }
  }
);
function escreverJson(texto) {
  textoJson.value = texto;
  if (!texto.trim()) {
    jsonQuebrado.value = '';
    mudar(undefined);
    return;
  }
  try {
    const valor = JSON.parse(texto);
    jsonQuebrado.value = '';
    mudar(valor);
  } catch (e) {
    jsonQuebrado.value = t('POWER_TOOLS.FLOW_EDITOR.TEST.INPUT.INVALID_JSON', {
      detalhe: e.message,
    });
  }
}
const nomeDoCampo = computed(() => props.rotulo || props.rotuloAcessivel);
</script>

<template>
  <div class="flex flex-col min-w-0 gap-1.5" :data-campo-do-teste="id">
    <div
      v-if="rotulo"
      class="flex flex-wrap items-baseline gap-x-2 text-xs text-n-slate-11"
    >
      <component
        :is="['array', 'object'].includes(tipo) || emJson ? 'span' : 'label'"
        :id="`${id}-rotulo`"
        :for="['array', 'object'].includes(tipo) || emJson ? undefined : id"
        class="font-mono text-sm font-medium text-n-slate-12"
      >
        {{ rotulo }}
      </component>
      <span v-if="nomeDoTipo">{{ nomeDoTipo }}</span>
      <span v-if="obrigatorio" data-obrigatorio>
        {{ t('POWER_TOOLS.FLOW_EDITOR.TEST.FORM.REQUIRED') }}
      </span>
    </div>

    <CodigoJson
      v-if="emJson"
      :id-do-campo="id"
      :model-value="textoJson"
      :rotulo="nomeDoCampo"
      :erro="!!meuErro || !!jsonQuebrado"
      data-campo
      @update:model-value="escreverJson"
      @sair="emit('sair')"
    />
    <CampoDeEscolha
      v-else-if="opcoes"
      :id="id"
      :model-value="escolhido"
      :options="opcoesDaEscolha"
      :erro="!!meuErro"
      :aria-label="rotulo ? undefined : rotuloAcessivel"
      :aria-invalid="!!meuErro || undefined"
      :aria-describedby="descritoPor"
      data-campo
      @update:model-value="escolher"
      @sair="emit('sair')"
    />
    <label
      v-else-if="tipo === 'boolean'"
      class="inline-flex items-center gap-2.5 self-start text-sm text-n-slate-12"
    >
      <Switch
        :id="id"
        :model-value="valor === true"
        :aria-label="nomeDoCampo"
        :aria-describedby="descritoPor"
        data-campo
        @update:model-value="alternar"
      />
      <span v-if="descricao" :id="idDaDescricao" class="text-n-slate-11">
        {{ descricao }}
      </span>
    </label>
    <Input
      v-else-if="tipo === 'integer' || tipo === 'number'"
      :id="id"
      :model-value="valor ?? ''"
      type="number"
      size="sm"
      :step="tipo === 'integer' ? 1 : 'any'"
      :message-type="meuErro ? 'error' : 'info'"
      :aria-label="rotulo ? undefined : rotuloAcessivel"
      :aria-invalid="!!meuErro || undefined"
      :aria-describedby="descritoPor"
      class="max-w-[12rem]"
      data-campo
      @update:model-value="escreverNumero"
      @blur="emit('sair')"
    />
    <div
      v-else-if="tipo === 'array'"
      role="group"
      :aria-labelledby="rotulo ? `${id}-rotulo` : undefined"
      :aria-label="rotulo ? undefined : rotuloAcessivel"
      :aria-describedby="descritoPor"
      class="flex flex-col gap-2"
      data-lista
    >
      <div
        v-for="(item, i) in itens"
        :key="i"
        class="flex items-start gap-1.5"
        :data-item="i"
      >
        <CampoDoTeste
          class="flex-1"
          :def="def?.items || null"
          :valor="item"
          :caminho="[...caminho, i]"
          :rotulo-acessivel="
            t('POWER_TOOLS.FLOW_EDITOR.TEST.FORM.ITEM_LABEL', {
              nome: nomeDoCampo,
              n: i + 1,
            })
          "
          :erro="erro"
          :prefixo="prefixo"
          @update:valor="valor => trocarItem(i, valor)"
          @sair="emit('sair')"
        />
        <span class="relative flex group/excluir">
          <button
            type="button"
            class="flex items-center justify-center p-0 rounded-lg size-8 shrink-0 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :aria-label="
              t('POWER_TOOLS.FLOW_EDITOR.TEST.FORM.DELETE_ITEM_LABEL', {
                nome: nomeDoCampo,
                n: i + 1,
              })
            "
            data-excluir-item
            @click="excluirItem(i)"
          >
            <span class="i-lucide-trash-2 size-4" aria-hidden="true" />
          </button>
          <span
            role="tooltip"
            class="absolute z-10 px-2 py-1 mb-1.5 text-xs font-medium leading-4 -translate-x-1/2 rounded-md opacity-0 pointer-events-none bottom-full left-1/2 w-max bg-n-slate-12 text-n-slate-1 transition-opacity group-hover/excluir:opacity-100 group-focus-within/excluir:opacity-100"
          >
            {{ t('POWER_TOOLS.FLOW_EDITOR.TEST.FORM.DELETE_ITEM') }}
          </span>
        </span>
      </div>
      <Button
        type="button"
        size="xs"
        variant="faded"
        color="slate"
        icon="i-lucide-plus"
        class="self-start"
        :label="t('POWER_TOOLS.FLOW_EDITOR.TEST.FORM.ADD_ITEM')"
        data-adicionar-item
        @click="adicionarItem"
      />
    </div>
    <div
      v-else-if="tipo === 'object'"
      role="group"
      :aria-labelledby="rotulo ? `${id}-rotulo` : undefined"
      :aria-label="rotulo ? undefined : rotuloAcessivel"
      :aria-describedby="descritoPor"
      class="flex flex-col gap-3 p-3 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
      data-grupo
    >
      <CampoDoTeste
        v-for="(sub, nome) in propriedades"
        :key="nome"
        :def="sub"
        :valor="valor?.[nome]"
        :caminho="[...caminho, nome]"
        :rotulo="nome"
        :obrigatorio="obrigatoriasDoObjeto.includes(nome)"
        :erro="erro"
        :prefixo="prefixo"
        @update:valor="v => trocarPropriedade(nome, v)"
        @sair="emit('sair')"
      />
    </div>
    <Input
      v-else
      :id="id"
      :model-value="typeof valor === 'string' ? valor : (valor ?? '')"
      size="sm"
      spellcheck="false"
      :message-type="meuErro ? 'error' : 'info'"
      :aria-label="rotulo ? undefined : rotuloAcessivel"
      :aria-invalid="!!meuErro || undefined"
      :aria-describedby="descritoPor"
      data-campo
      @update:model-value="mudar"
      @blur="emit('sair')"
    />

    <AvisoDoFlow
      v-if="meuErro || jsonQuebrado"
      :id="idDoErro"
      solto
      data-erro-do-campo
    >
      <TextoDoCompilador :texto="meuErro || jsonQuebrado" />
    </AvisoDoFlow>
    <span
      v-if="descricao && tipo !== 'boolean'"
      :id="idDaDescricao"
      class="text-xs text-n-slate-11"
    >
      {{ descricao }}
    </span>
  </div>
</template>
